<template>
  <v-container fluid class="pa-6">
    <!-- Header Section -->
    <v-row class="mb-6">
      <v-col cols="12">
        <div class="text-center">
          <v-icon size="48" color="primary" class="mb-2">mdi-car-search</v-icon>
          <h1 class="text-h4 font-weight-light mb-2">Vehicle Inventory Lookup</h1>
          <p class="text-subtitle-1 text--secondary">
            Search vehicle data from your inventory using VIN
          </p>
        </div>
      </v-col>
    </v-row>

    <!-- Search Section -->
    <v-row justify="center" class="mb-8">
      <v-col cols="12" sm="10" md="8" lg="6">
        <v-card elevation="2" class="pa-4">
          <v-card-title class="justify-center pb-2">
            <v-icon left color="primary">mdi-magnify</v-icon>
            Vehicle Identification Number Lookup
          </v-card-title>

          <v-form @submit.prevent="onSearch">
            <v-text-field
              v-model="vin"
              label="Enter VIN"
              placeholder="e.g., 1G1YC2D46M5106750"
              outlined
              dense
              clearable
              :loading="loading"
              :rules="vinRules"
              counter="17"
              maxlength="17"
              prepend-inner-icon="mdi-barcode"
              class="mb-4"
            />

            <div class="text-center">
              <v-btn
                color="primary"
                type="submit"
                size="large"
                :loading="loading"
                :disabled="!vin || vin.length !== 17"
                elevation="2"
              >
                <v-icon left>mdi-car-search</v-icon>
                Search Inventory
              </v-btn>
            </div>
          </v-form>
        </v-card>
      </v-col>
    </v-row>

    <!-- Loading State -->
    <v-row v-if="loading" justify="center" class="mb-8">
      <v-col cols="12" class="text-center">
        <v-progress-circular indeterminate color="primary" size="64" width="4" />
        <p class="text-h6 mt-4">Searching inventory data...</p>
      </v-col>
    </v-row>

    <!-- Results Section -->
    <v-fade-transition>
      <div v-if="fields.length && !loading">
        <!-- Results Header -->
        <v-row class="mb-4">
          <v-col cols="12">
            <v-card color="primary" dark elevation="4">
              <v-card-title class="justify-center">
                <v-icon left size="28">mdi-chart-timeline-variant</v-icon>
                <span class="text-h5">Inventory Data Found</span>
              </v-card-title>
              <v-card-subtitle class="text-center pb-4">
                Found {{ fields.length }} data fields across {{ feeds.length }} inventory sources
              </v-card-subtitle>
            </v-card>
          </v-col>
        </v-row>

        <!-- Feed Legend -->
        <v-row class="mb-4">
          <v-col cols="12">
            <v-card outlined class="pa-4">
              <v-card-title class="text-h6 pb-3">
                <v-icon left color="info">mdi-information</v-icon>
                Inventory Sources
              </v-card-title>
              <div class="d-flex flex-wrap gap-2">
                <v-chip
                  v-for="feed in feeds"
                  :key="feed"
                  :color="getFeedColor(feed)"
                  outlined
                  label
                  class="ma-1"
                >
                  <v-icon left small>mdi-database</v-icon>
                  {{ feed }}
                </v-chip>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Enhanced Results Table -->
        <v-row>
          <v-col cols="12">
            <v-card elevation="3">
              <v-card-title class="sticky-header d-flex justify-space-between justify-center">
                <div class="d-flex justify-center">
                  <v-icon left>mdi-table</v-icon>
                  <span>Vehicle Data Comparison</span>
                </div>
                <v-btn icon small @click="exportData" color="primary">
                  <v-icon>mdi-download</v-icon>
                </v-btn>
              </v-card-title>

              <v-data-table
                :headers="tableHeaders"
                :items="tableItems"
                :items-per-page="-1"
                hide-default-footer
                class="enhanced-table"
                :mobile-breakpoint="0"
              >
                <!-- Field Name Column -->
                <template #[`item.fieldName`]="{ item }">
                  <div class="field-name-cell d-flex justify-center">
                    <span class="font-weight-medium">{{ item.displayName }}</span>
                  </div>
                </template>

                <!-- Current Value Column (always green/primary) -->
                <template #[`item.currentValue`]="{ item }">
                  <div class="feed-cell primary-source current-value-cell">
                    <div class="feed-chip-container">
                      <v-badge color="success" icon="mdi-star" overlap offset-x="8" offset-y="8">
                        <v-chip
                          :color="getFeedColor(item.currentSource.feed)"
                          small
                          outlined
                          class="ma-1"
                        >
                          <v-icon left x-small>mdi-database</v-icon>
                          {{ item.currentSource.feed }}
                        </v-chip>
                      </v-badge>
                    </div>

                    <div class="value-container mt-2">
                      <v-chip color="success" small class="font-weight-bold">
                        {{ item.currentValue || 'N/A' }}
                      </v-chip>
                      <div class="segment-info mt-1">
                        <v-chip x-small color="info" outlined>
                          Segment: {{ item.currentSource.segment }}
                        </v-chip>
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Dynamic Feed Columns -->
                <template v-for="feed in feeds" :key="`item.${feed}`" #[`item.${feed}`]="{ item }">
                  <div class="feed-cell">
                    <div class="feed-chip-container">
                      <v-chip
                        :color="getFeedColor(feed)"
                        small
                        outlined
                        class="ma-1"
                        :class="{ 'feed-chip-dimmed': item.currentSource.feed !== feed }"
                      >
                        <v-icon left x-small>mdi-database</v-icon>
                        {{ feed }}
                      </v-chip>
                    </div>

                    <!-- Value Display with consistent structure -->
                    <div class="value-container mt-2">
                      <template v-if="item.currentSource.feed === feed">
                        <!-- Show current value (same as Current Value column) -->
                        <v-chip small outlined color="success" class="font-weight-medium">
                          {{ item.currentValue || 'N/A' }}
                        </v-chip>
                        <div class="field-mapping mt-1">
                          <v-chip x-small color="grey" outlined>
                            {{ item.currentSource.fieldName }}
                          </v-chip>
                        </div>
                      </template>

                      <template v-else-if="getOther(item, feed)">
                        <!-- Show alternative value from other source -->
                        <v-chip small outlined class="alternative-value">
                          {{ getOther(item, feed)?.value || 'N/A' }}
                        </v-chip>
                        <div class="field-mapping mt-1">
                          <v-tooltip bottom>
                            <template #activator="{ props }">
                              <v-chip x-small color="grey" outlined v-bind="props">
                                {{ truncateText(getOther(item, feed)?.fieldName, 15) }}
                              </v-chip>
                            </template>
                            <span>{{ getOther(item, feed)?.fieldName }}</span>
                          </v-tooltip>
                        </div>
                      </template>

                      <template v-else>
                        <!-- No data for this feed -->
                        <div class="no-data-container">
                          <v-chip small color="grey lighten-2" text-color="grey darken-2">
                            <v-icon left x-small>mdi-minus</v-icon>
                            No Data
                          </v-chip>
                          <div class="segment-placeholder mt-1">
                            <v-chip
                              x-small
                              outlined
                              color="transparent"
                              text-color="transparent"
                              class="invisible-chip"
                            >
                              Placeholder
                            </v-chip>
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>
                </template>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </v-fade-transition>

    <!-- Empty State -->
    <v-row v-if="!fields.length && !loading" justify="center">
      <v-col cols="12" sm="8" md="6" class="text-center">
        <v-card outlined class="pa-8">
          <v-icon size="80" color="grey lighten-1" class="mb-4">mdi-car-off</v-icon>
          <h3 class="text-h6 mb-2">No Vehicle Found</h3>
          <p class="text-body-2 text--secondary">
            Enter a valid 17-character VIN above to search your vehicle inventory
          </p>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { fetchISEMappedFieldAnalysis } from '@/services/vehicleFieldValuesPreview'
import { useAccountSettingsStore } from '@/stores/accountSettings'

interface SourceInfo {
  feed: string
  fieldName: string
  segment?: string
  value?: string
}

interface FieldData {
  name: string
  displayName: string
  currentValue: string
  currentSource: {
    feed: string
    fieldName: string
    segment: string
  }
  otherSources: SourceInfo[]
}

const vin = ref<string>('')
const fields = ref<FieldData[]>([])
const loading = ref<boolean>(false)
const accountSettings = useAccountSettingsStore()
const endpoint = import.meta.env.VITE_ISE_BACKEND_URL

// VIN validation rules
const vinRules = [
  (v: string) => !!v || 'VIN is required',
  (v: string) => v.length === 17 || 'VIN must be exactly 17 characters',
  (v: string) => /^[A-HJ-NPR-Z0-9]+$/i.test(v) || 'VIN contains invalid characters',
]

// Feed colors for consistent styling
const feedColors = ['primary', 'secondary', 'accent', 'info', 'warning', 'error', 'success']

const getFeedColor = (feed: string): string => {
  const index = feeds.value.indexOf(feed)
  return feedColors[index % feedColors.length]
}

// Compute unique list of all feeds (current + other), ordered by appearance
const feeds = computed<string[]>(() => {
  const set = new Set<string>()
  fields.value.forEach((f) => {
    set.add(f.currentSource.feed)
    f.otherSources.forEach((src) => set.add(src.feed))
  })
  return Array.from(set)
})

// Table headers for v-data-table
const tableHeaders = computed(() => {
  const headers = [
    {
      text: 'Field Name',
      value: 'fieldName',
      sortable: true,
      align: 'center' as const,
      width: '200px',
    },
    {
      text: 'Current Value',
      value: 'currentValue',
      sortable: false,
      align: 'center' as const,
      width: '250px',
    },
  ]

  feeds.value.forEach((feed) => {
    headers.push({
      text: feed,
      value: feed,
      sortable: false,
      align: 'center' as const,
      width: '250px',
    })
  })

  return headers
})

// Transform fields data for v-data-table
const tableItems = computed(() => {
  return fields.value.map((field) => {
    const item: any = {
      fieldName: field.name,
      displayName: field.displayName,
      currentValue: field.currentValue,
      currentSource: field.currentSource,
      otherSources: field.otherSources,
    }

    // Add feed columns
    feeds.value.forEach((feed) => {
      item[feed] = field // Store the whole field for template access
    })

    return item
  })
})

// Helper to get otherSources entry for a given field and feed
const getOther = (field: FieldData, feed: string): SourceInfo | undefined => {
  return field.otherSources.find((src) => src.feed === feed)
}

// Helper to truncate text
const truncateText = (text: string | undefined, maxLength: number): string => {
  if (!text) return ''
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
}

// Export functionality
const exportData = () => {
  const csvContent = generateCSV()
  const blob = new Blob([csvContent], { type: 'text/csv' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `vehicle-inventory-${vin.value}-${new Date().toISOString().split('T')[0]}.csv`
  link.click()
  window.URL.revokeObjectURL(url)
}

const generateCSV = (): string => {
  const headers = ['Field Name', ...feeds.value]
  const rows = fields.value.map((field) => {
    const row = [field.displayName]
    feeds.value.forEach((feed) => {
      if (field.currentSource.feed === feed) {
        row.push(`${field.currentValue} (Primary - ${field.currentSource.segment})`)
      } else {
        const other = getOther(field, feed)
        row.push(other ? `${other.value} (${other.fieldName})` : 'No Data')
      }
    })
    return row
  })

  return [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n')
}

const onSearch = async () => {
  if (!vin.value || vin.value.length !== 17) return

  loading.value = true
  try {
    const mappedFieldAnalysis = await fetchISEMappedFieldAnalysis(
      endpoint,
      accountSettings.accountId,
      vin.value,
    )

    let responseFields = {}

    if (mappedFieldAnalysis && mappedFieldAnalysis.fields) {
      responseFields = mappedFieldAnalysis.fields
    }

    fields.value = Object.entries(responseFields).map(([key, data]: any) => ({
      name: key,
      displayName: key.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
      currentValue: data.currentValue,
      currentSource: data.currentSource,
      otherSources: data.otherSources || [],
    }))
  } catch (error) {
    console.error('Error fetching inventory data:', error)
    // You might want to show an error message to the user here
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.sticky-header {
  position: sticky;
  top: 0;
  z-index: 2;
  background: white;
}

.enhanced-table {
  border-radius: 8px;
}

.field-name-cell {
  display: flex;
  align-items: center;
  min-height: 40px;
}

.current-value-cell {
  background-color: rgba(76, 175, 80, 0.15) !important;
  border-left: 4px solid #4caf50 !important;
  border-right: 2px solid #4caf50;
}

.feed-chip-dimmed {
  opacity: 0.7;
}

.feed-cell {
  padding: 12px 8px;
  text-align: center;
  transition: background-color 0.2s ease;
  min-height: 120px; /* Ensure consistent height */
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.feed-cell.primary-source {
  background-color: rgba(76, 175, 80, 0.1);
  border-left: 3px solid #4caf50;
}

.feed-chip-container {
  min-height: 32px; /* Reserve space for feed chip */
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.feed-placeholder {
  visibility: hidden; /* Hide but maintain space */
}

.value-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  flex: 1; /* Take remaining space */
  justify-content: flex-start;
}

.value-container :deep(.v-chip__content) {
  overflow: auto;
  max-width: 200px;
}

.no-data-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.segment-info,
.field-mapping,
.segment-placeholder {
  display: flex;
  justify-content: center;
  min-height: 24px; /* Consistent height for segment area */
}

.invisible-chip {
  opacity: 0 !important;
  pointer-events: none;
}

.alternative-value {
  opacity: 0.8;
}

.gap-2 > * {
  margin: 4px;
}

/* Mobile responsiveness */
@media (max-width: 960px) {
  .v-data-table {
    font-size: 0.85rem;
  }

  .feed-cell {
    padding: 8px 4px;
  }
}
</style>
